import {cloneDeep} from 'lodash';
import {IntlShape} from 'react-intl';

import {idFromGlobalId} from '../../../forecast-app/shared/util/GlobalIdUtil';
import Util from '../../../forecast-app/shared/util/util';
import {estimateIsInDays} from '../../baseline/Util';
import {BaselinePDFItemType, BaselinePDFPhaseType, BaselinePDFProjectType} from './BaselinePDFTypes';

// The shapes the Baseline page's Relay data has. Relay types every connection, edge and node as
// nullable, so these accept nulls and the formatters skip anything missing.
type PhaseBaselineRoleEdges = ReadonlyArray<{readonly node: PhaseBaselineRoleRelay | null} | null>;
type PhaseBaselineExpenseEdges = ReadonlyArray<{readonly node: PhaseBaselineExpenseRelay | null} | null>;

type PhaseRelay = {
	readonly node: {
		readonly id: string;
		readonly name?: string | null;
		readonly phaseBaselineRoles?: {readonly edges: PhaseBaselineRoleEdges | null} | null;
		readonly phaseBaselineExpenses?: {readonly edges: PhaseBaselineExpenseEdges | null} | null;
	} | null;
} | null;

type PhaseBaselineRoleRelay = {
	readonly id: string;
	readonly role?: {
		readonly name?: string | null;
	} | null;
	readonly baselineMinutes?: number | null;
};

type PhaseBaselineExpenseRelay = {
	readonly id: string;
	readonly expenseCategory?: {
		readonly name?: string | null;
	} | null;
};

type PhaseFinancials = {
	readonly totalBaselineRevenue: number;
	readonly roles: ReadonlyMap<number, RoleFinancials>;
	readonly expenses: ReadonlyMap<number, ExpenseFinancials>;
};

type RoleFinancials = {
	readonly baselineRatePerHour: number | null;
	readonly baselineRevenue: number | null;
};

type ExpenseFinancials = {
	readonly baselineRevenue: number | null;
};

type FormatterBase = {
	currencySymbol: string;
	intl: IntlShape;
	minutesPerDay: number | null;
};

interface FormatPhaseBaselineRoleInput extends FormatterBase {
	phaseBaselineRole: PhaseBaselineRoleRelay;
	roleFinancials?: RoleFinancials;
}
const formatPhaseBaselineRole = ({
	phaseBaselineRole,
	roleFinancials,
	currencySymbol,
	intl,
	minutesPerDay,
}: FormatPhaseBaselineRoleInput): BaselinePDFItemType => {
	const baselineRatePerHour = roleFinancials?.baselineRatePerHour ?? 0;
	const baselineRevenue = roleFinancials?.baselineRevenue ?? 0;
	const baselineMinutes = phaseBaselineRole.baselineMinutes ?? 0;
	const hours = estimateIsInDays(minutesPerDay)
		? String(Math.round((baselineMinutes / minutesPerDay) * 100) / 100)
		: String(baselineMinutes / 60);
	return {
		id: phaseBaselineRole.id,
		name: phaseBaselineRole.role?.name ?? '',
		hours,
		hourlyRate: Util.getFormattedCurrencyValue(
			currencySymbol,
			intl.formatNumber(baselineRatePerHour, {format: 'always_two_decimal'})
		),
		totalPrice: Util.getFormattedCurrencyValue(
			currencySymbol,
			intl.formatNumber(baselineRevenue, {format: 'always_two_decimal'})
		),
	};
};

interface FormatPhaseBaselineExpenseInput extends FormatterBase {
	phaseBaselineExpense: PhaseBaselineExpenseRelay;
	expenseFinancials?: ExpenseFinancials;
}
const formatPhaseBaselineExpense = ({
	phaseBaselineExpense,
	expenseFinancials,
	currencySymbol,
	intl,
}: FormatPhaseBaselineExpenseInput): BaselinePDFItemType => {
	const baselineRevenue = expenseFinancials?.baselineRevenue ?? 0;
	return {
		id: phaseBaselineExpense.id,
		name: `Expense: ${phaseBaselineExpense.expenseCategory?.name ?? ''}`,
		hours: '-',
		hourlyRate: '-',
		totalPrice: Util.getFormattedCurrencyValue(
			currencySymbol,
			intl.formatNumber(baselineRevenue, {format: 'always_two_decimal'})
		),
	};
};

interface FormatPhaseBaselineItemsInput extends FormatterBase {
	phaseBaselineRoles?: PhaseBaselineRoleEdges | null;
	phaseBaselineExpenses?: PhaseBaselineExpenseEdges | null;
	phaseFinancialNumbers?: PhaseFinancials;
}
const formatPhaseBaselineItems = ({
	phaseBaselineRoles,
	phaseBaselineExpenses,
	phaseFinancialNumbers,
	currencySymbol,
	intl,
	minutesPerDay,
}: FormatPhaseBaselineItemsInput): BaselinePDFItemType[] => {
	const phaseBaselineRoleItems = phaseBaselineRoles
		? phaseBaselineRoles
				.flatMap(edge => (edge?.node ? [edge.node] : []))
				.map(phaseBaselineRole => {
					const phaseBaselineRoleId = idFromGlobalId(phaseBaselineRole.id);
					const roleFinancials = phaseFinancialNumbers?.roles.get(phaseBaselineRoleId);
					return formatPhaseBaselineRole({phaseBaselineRole, roleFinancials, currencySymbol, intl, minutesPerDay});
				})
		: [];
	const phaseBaselineExpenseItems = phaseBaselineExpenses
		? phaseBaselineExpenses
				.flatMap(edge => (edge?.node ? [edge.node] : []))
				.map(phaseBaselineExpense => {
					const phaseBaselineExpenseId = idFromGlobalId(phaseBaselineExpense.id);
					const expenseFinancials = phaseFinancialNumbers?.expenses.get(phaseBaselineExpenseId);
					return formatPhaseBaselineExpense({
						phaseBaselineExpense,
						expenseFinancials,
						currencySymbol,
						intl,
						minutesPerDay,
					});
				})
		: [];

	return [...phaseBaselineRoleItems, ...phaseBaselineExpenseItems];
};

interface FormatPhasesInput extends FormatterBase {
	phases: ReadonlyArray<PhaseRelay>;
	phaseSortOrder: string[];
	groupedFinancialNumbers: ReadonlyMap<number | null, PhaseFinancials>;
}
export const formatPhases = ({
	phases,
	groupedFinancialNumbers,
	phaseSortOrder,
	currencySymbol,
	intl,
	minutesPerDay,
}: FormatPhasesInput): BaselinePDFPhaseType[] => {
	return cloneDeep(phases.flatMap(edge => (edge?.node ? [edge.node] : [])))
		.sort((a, b) => phaseSortOrder.indexOf(a.id) - phaseSortOrder.indexOf(b.id))
		.map(phase => {
			const phaseId = idFromGlobalId(phase.id);
			const phaseFinancialNumbers = groupedFinancialNumbers.get(phaseId);
			const phaseTotal = phaseFinancialNumbers?.totalBaselineRevenue ?? 0;
			return {
				id: phase.id,
				name: phase.name ?? '',
				total: Util.getFormattedCurrencyValue(
					currencySymbol,
					intl.formatNumber(phaseTotal, {format: 'always_two_decimal'})
				),
				items: formatPhaseBaselineItems({
					phaseBaselineRoles: phase.phaseBaselineRoles?.edges,
					phaseBaselineExpenses: phase.phaseBaselineExpenses?.edges,
					phaseFinancialNumbers,
					currencySymbol,
					intl,
					minutesPerDay,
				}),
			};
		});
};

interface FormatProjectInput extends FormatterBase {
	project: {name: string | null};
	totalFinancialNumbers?: {totalBaselineRevenue?: number};
}
export const formatProject = ({
	project,
	totalFinancialNumbers,
	currencySymbol,
	intl,
	minutesPerDay,
}: FormatProjectInput): BaselinePDFProjectType => {
	const projectTotal = totalFinancialNumbers?.totalBaselineRevenue ?? 0;
	return {
		name: project.name ?? '',
		total: Util.getFormattedCurrencyValue(currencySymbol, intl.formatNumber(projectTotal, {format: 'always_two_decimal'})),
		minutesPerDay,
	};
};
